import React, { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { MessageCircle, X, Send, Loader2, Sparkles, ChevronDown, ChevronUp, Mic, MicOff, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AIAssistant = ({
  pageType = 'general',
  pageContextSummary = '',
  userProfile = {},
  artifacts = {}
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [speechSupported, setSpeechSupported] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const recognitionRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Load session from localStorage on mount
  useEffect(() => {
    const savedSessionId = localStorage.getItem('ai_assistant_session_id');
    if (savedSessionId) {
      setSessionId(savedSessionId);
      // Try to load previous messages
      loadSessionHistory(savedSessionId);
    }
  }, []);

  const loadSessionHistory = async (sid) => {
    try {
      const response = await fetch(`${API_URL}/api/ai-assistant/session/${sid}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        if (data.messages && data.messages.length > 0) {
          setMessages(data.messages.map(msg => ({
            ...msg,
            timestamp: new Date(msg.timestamp)
          })));
        }
      }
    } catch (error) {
      console.error('Failed to load session history:', error);
    }
  };

  // Initialize speech recognition
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (SpeechRecognition) {
        setSpeechSupported(true);
        const recognition = new SpeechRecognition();
        recognition.continuous = false;
        recognition.interimResults = false;
        recognition.lang = 'en-US';

        recognition.onresult = (event) => {
          const transcript = event.results[0][0].transcript;
          setInputMessage(transcript);
          setIsListening(false);
        };

        recognition.onerror = (event) => {
          console.error('Speech recognition error:', event.error);
          setIsListening(false);
          if (event.error === 'not-allowed') {
            toast.error('Microphone access denied. Please enable microphone permissions.');
          } else if (event.error === 'no-speech') {
            toast.error('No speech detected. Please try again.');
          } else {
            toast.error('Speech recognition error. Please try again.');
          }
        };

        recognition.onend = () => {
          setIsListening(false);
        };

        recognitionRef.current = recognition;
      }
    }
  }, []);

  const toggleVoiceInput = () => {
    if (!speechSupported) {
      toast.error('Speech recognition is not supported in your browser. Please use Chrome, Edge, or Safari.');
      return;
    }

    if (isListening) {
      recognitionRef.current?.stop();
      setIsListening(false);
    } else {
      try {
        recognitionRef.current?.start();
        setIsListening(true);
        toast.info('Listening... Speak now');
      } catch (error) {
        console.error('Error starting speech recognition:', error);
        toast.error('Could not start voice input. Please try again.');
      }
    }
  };

  useEffect(() => {
    if (isOpen && !isMinimized && messages.length === 0) {
      // Show welcome message when first opened
      const welcomeMessage = {
        role: 'assistant',
        content: `Hi! I'm your CareerSupport.io AI Assistant. I'm here to help you with:

• **Platform Navigation** - How to use any tool or feature
• **Resume Writing** - Creating compelling, ATS-optimized resumes
• **Job Search** - Finding opportunities and tracking applications
• **Interview Prep** - Common questions and how to answer them
• **Career Planning** - Setting goals and developing skills
• **Remote Work** - Finding and succeeding in WFH positions

What can I help you with today?`,
        timestamp: new Date()
      };

      setMessages([welcomeMessage]);
    }
  }, [isOpen, isMinimized, messages.length]);

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const userMessage = {
      role: 'user',
      content: inputMessage,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    const messageToSend = inputMessage;
    setInputMessage('');
    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/ai-assistant/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          message: messageToSend,
          session_id: sessionId
        })
      });

      if (response.ok) {
        const data = await response.json();
        
        // Save session ID
        if (data.session_id && data.session_id !== sessionId) {
          setSessionId(data.session_id);
          localStorage.setItem('ai_assistant_session_id', data.session_id);
        }

        const assistantMessage = {
          role: 'assistant',
          content: data.response,
          timestamp: new Date()
        };

        setMessages(prev => [...prev, assistantMessage]);
      } else {
        throw new Error('Failed to get response');
      }
    } catch (error) {
      console.error('AI Assistant error:', error);
      toast.error('Failed to get response. Please try again.');
      
      const errorMessage = {
        role: 'assistant',
        content: "I'm sorry, I encountered an error. Please try rephrasing your question or try again in a moment. If the issue persists, you can contact support at support@careersupport.io.",
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleClearChat = async () => {
    if (sessionId) {
      try {
        await fetch(`${API_URL}/api/ai-assistant/session/${sessionId}`, {
          method: 'DELETE',
          credentials: 'include'
        });
      } catch (error) {
        console.error('Failed to clear session:', error);
      }
    }
    setMessages([]);
    setSessionId(null);
    localStorage.removeItem('ai_assistant_session_id');
    setIsOpen(false);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  // Format message content with markdown-like styling
  const formatContent = (content) => {
    // Convert **text** to bold
    let formatted = content.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    // Convert bullet points
    formatted = formatted.replace(/^• /gm, '• ');
    return formatted;
  };

  if (!isOpen) {
    return (
      <div className="fixed bottom-6 right-6 z-50 flex flex-col items-center">
        <Button
          onClick={() => setIsOpen(true)}
          className="h-14 w-14 rounded-full shadow-lg bg-gradient-to-r from-purple-600 to-sky-600 hover:from-purple-700 hover:to-sky-700 text-white"
          title="Open AI Assistant"
          data-testid="ai-assistant-button"
        >
          <MessageCircle className="w-6 h-6" />
        </Button>
        <span className="mt-2 text-xs font-semibold text-gray-700 bg-white px-3 py-1 rounded-full shadow-md whitespace-nowrap">
          AI Assistant
        </span>
      </div>
    );
  }

  return (
    <div className="fixed bottom-6 right-6 z-50">
      <div className={`bg-white rounded-2xl shadow-2xl border border-gray-200 transition-all ${
        isMinimized ? 'w-80 h-16' : 'w-96 h-[600px]'
      } flex flex-col`}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 bg-gradient-to-r from-purple-600 to-sky-600 rounded-t-2xl text-white">
          <div className="flex items-center space-x-2">
            <Sparkles className="w-5 h-5" />
            <div>
              <h3 className="font-semibold text-sm">Career Support AI</h3>
              <p className="text-xs text-purple-100">Your 24/7 career assistant</p>
            </div>
          </div>
          <div className="flex items-center space-x-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClearChat}
              className="h-8 w-8 p-0 text-white hover:bg-white/20"
              title="Clear conversation"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsMinimized(!isMinimized)}
              className="h-8 w-8 p-0 text-white hover:bg-white/20"
            >
              {isMinimized ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClose}
              className="h-8 w-8 p-0 text-white hover:bg-white/20"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {!isMinimized && (
          <>
            {/* Messages Area */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
              {messages.map((msg, idx) => (
                <div
                  key={idx}
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[85%] rounded-2xl px-4 py-3 ${
                      msg.role === 'user'
                        ? 'bg-gradient-to-r from-purple-600 to-sky-600 text-white'
                        : 'bg-white border border-gray-200 text-gray-800'
                    }`}
                  >
                    <div 
                      className="text-sm whitespace-pre-wrap"
                      dangerouslySetInnerHTML={{ __html: formatContent(msg.content) }}
                    />
                    <div className={`text-xs mt-1 ${
                      msg.role === 'user' ? 'text-purple-100' : 'text-gray-500'
                    }`}>
                      {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </div>
                </div>
              ))}
              
              {isLoading && (
                <div className="flex justify-start">
                  <div className="bg-white border border-gray-200 rounded-2xl px-4 py-3 flex items-center space-x-2">
                    <Loader2 className="w-4 h-4 animate-spin text-purple-600" />
                    <span className="text-sm text-gray-600">Thinking...</span>
                  </div>
                </div>
              )}
              
              <div ref={messagesEndRef} />
            </div>

            {/* Quick Actions */}
            {messages.length <= 1 && (
              <div className="px-4 pb-2">
                <p className="text-xs text-gray-500 mb-2">Quick questions:</p>
                <div className="flex flex-wrap gap-1">
                  {[
                    "How do I use the ATS Scanner?",
                    "Resume writing tips",
                    "Interview prep help"
                  ].map((question, idx) => (
                    <button
                      key={idx}
                      onClick={() => {
                        setInputMessage(question);
                        setTimeout(() => handleSendMessage(), 100);
                      }}
                      className="text-xs bg-gray-100 hover:bg-gray-200 px-2 py-1 rounded-full text-gray-700 transition-colors"
                    >
                      {question}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Input Area */}
            <div className="p-4 bg-white border-t border-gray-200 rounded-b-2xl">
              <div className="flex items-end space-x-2">
                <Textarea
                  ref={textareaRef}
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder={isListening ? "Listening... speak now" : "Type your question..."}
                  className="flex-1 min-h-[44px] max-h-32 resize-none text-sm"
                  rows={1}
                  disabled={isLoading || isListening}
                  data-testid="ai-assistant-input"
                />
                {speechSupported && (
                  <Button
                    onClick={toggleVoiceInput}
                    disabled={isLoading}
                    className={`h-11 w-11 p-0 rounded-full flex-shrink-0 transition-all ${
                      isListening 
                        ? 'bg-red-500 hover:bg-red-600 animate-pulse' 
                        : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                    }`}
                    title={isListening ? "Stop recording" : "Voice input"}
                  >
                    {isListening ? (
                      <MicOff className="w-5 h-5 text-white" />
                    ) : (
                      <Mic className="w-5 h-5" />
                    )}
                  </Button>
                )}
                <Button
                  onClick={handleSendMessage}
                  disabled={!inputMessage.trim() || isLoading || isListening}
                  className="h-11 w-11 p-0 rounded-full bg-gradient-to-r from-purple-600 to-sky-600 hover:from-purple-700 hover:to-sky-700 flex-shrink-0"
                  data-testid="ai-assistant-send"
                >
                  {isLoading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Send className="w-5 h-5" />
                  )}
                </Button>
              </div>
              <p className="text-xs text-gray-500 mt-2 text-center">
                {isListening ? (
                  <span className="text-red-500 font-medium">🎤 Listening... Speak your question</span>
                ) : (
                  <>AI responses are for guidance only. Verify important information.</>
                )}
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AIAssistant;
